import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { API_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const FEE_TABS = [
  {
    key: "structure",
    label: "Fee Structure",
    icon: <AccountBalanceWalletIcon />,
  },
  {
    key: "bundles",
    label: "Fee Bundles",
    icon: <Inventory2Icon />,
  },
];

const emptyFeeStructure = (campusCode = "") => ({
  CampusCode: campusCode,
  FeeType: "",
  FeeAmount: "",
  Grace: "",
  DueDate: "",
  IsActive: 1,
});

const emptyBundle = {
  BundleId: null,
  BundleKey: "",
  StudentLabel: "",
  IsActive: 1,
  AllowPartial: 0,
  AllocationStrategy: "PRIORITY",
  SortOrder: 0,
  Items: [],
};

export default function StudentFeeSetup() {
  // Theme intentionally mirrors AcademicCodeSetup.
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const token = localStorage.getItem("auth") || localStorage.getItem("token");
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [tab, setTab] = useState(0);
  const activeTab = FEE_TABS[tab];

  const [campusCode, setCampusCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "success", text: "" });

  // ---------------- Fee Structure ----------------
  const [feeStructures, setFeeStructures] = useState([]);
  const [feeEditId, setFeeEditId] = useState(null);
  const [feeAddOpen, setFeeAddOpen] = useState(false);
  const [newFee, setNewFee] = useState(emptyFeeStructure(""));
  const [feePage, setFeePage] = useState(0);
  const [feeRowsPerPage, setFeeRowsPerPage] = useState(25);
  const feeFileInputRef = useRef(null);

  // ---------------- Fee Bundles ----------------
  const [feeTypes, setFeeTypes] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
  const [bundleDraft, setBundleDraft] = useState(emptyBundle);
  const [bundlePage, setBundlePage] = useState(0);
  const [bundleRowsPerPage, setBundleRowsPerPage] = useState(25);

  const pageWrapSx = {
    p: 2,
    backgroundColor: BG,
    minHeight: "100vh",
  };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
  };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg,${THEME_ORANGE},${THEME_ORANGE_2})`,
    },
  };

  // Popup cards use a top orange accent (not the page/card left accent).
  const popupSectionSx = {
    ...surfaceSx,
    p: 2.5,
    borderRadius: 2.5,
    borderTop: `4px solid ${THEME_ORANGE}`,
    overflow: "hidden",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
    borderColor: "rgba(25,118,210,0.35)",
    color: BLUE,
    backgroundColor: "white",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    color: BLUE,
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };

  const dangerIconBtnSx = {
    color: "rgba(244,67,54,0.90)",
    "&:hover": {
      backgroundColor: "rgba(244,67,54,0.08)",
      color: "rgba(244,67,54,1)",
    },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      whiteSpace: "nowrap",
      borderBottom: "1px solid rgba(255,255,255,0.18)",
    },
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": {
      borderBottom: "1px solid rgba(25,118,210,0.10)",
    },
  });

  const tabsSx = {
    px: 1,
    borderBottom: `1px solid ${BORDER}`,
    minHeight: 48,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 800,
      minHeight: 48,
    },
    "& .Mui-selected": {
      color: `${THEME_ORANGE} !important`,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: THEME_ORANGE,
      height: 3,
    },
  };

  const serialBadge = (number) => (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 900,
        fontSize: "1rem",
        color: "#1976D2",
        backgroundColor: "rgba(25,118,210,0.06)",
        border: "2px solid rgba(25,118,210,0.20)",
        lineHeight: 1,
      }}
    >
      {number}
    </Box>
  );

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const cc = decoded.campusCode || decoded.CampusCode || "";
      setCampusCode(cc);
      setNewFee(emptyFeeStructure(cc));
    } catch {
      setMsg({
        type: "error",
        text: "Failed to decode authentication token.",
      });
    }
  }, [token]);

  // ============================================================
  // FEE STRUCTURE
  // ============================================================

  const fetchFeeStructures = async () => {
    if (!campusCode) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/StudentFeeStructure`, {
        headers: authHeaders,
      });
      setFeeStructures(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to fetch fee structure.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campusCode) fetchFeeStructures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", {
          timeZone: "Asia/Kolkata",
        })
      : "";

  const updateExistingFee = (id, field, value) => {
    setFeeStructures((rows) =>
      rows.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const addFeeStructure = async () => {
    if (!String(newFee.FeeType || "").trim()) {
      return setMsg({ type: "error", text: "Fee Type is required." });
    }
    if (String(newFee.FeeAmount ?? "").trim() === "") {
      return setMsg({ type: "error", text: "Fee Amount is required." });
    }

    try {
      await axios.post(
        `${API_URL}/api/StudentFeeStructure`,
        { ...newFee, CampusCode: campusCode },
        { headers: authHeaders }
      );

      setNewFee(emptyFeeStructure(campusCode));
      setFeeAddOpen(false);
      setMsg({ type: "success", text: "Fee structure added successfully." });
      await fetchFeeStructures();
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          (e.response?.status === 409
            ? "Fee structure already exists."
            : "Failed to add fee structure."),
      });
    }
  };

  const updateFeeStructure = async (row) => {
    try {
      await axios.put(
        `${API_URL}/api/StudentFeeStructure/${row.id}`,
        row,
        { headers: authHeaders }
      );
      setFeeEditId(null);
      setMsg({
        type: "success",
        text: "Fee structure updated successfully.",
      });
      await fetchFeeStructures();
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          "Failed to update fee structure.",
      });
    }
  };

  const deleteFeeStructure = async (row) => {
    if (!window.confirm(`Delete fee type "${row.FeeType}"?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/StudentFeeStructure/${row.id}`,
        { headers: authHeaders }
      );
      setMsg({
        type: "success",
        text: "Fee structure deleted successfully.",
      });
      await fetchFeeStructures();
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          "Failed to delete fee structure.",
      });
    }
  };

  const downloadFeeSample = () => {
    const cc = campusCode || "CAMPUS01";
    const headers = [
      "CampusCode",
      "FeeType",
      "FeeAmount",
      "Grace",
      "DueDate",
      "IsActive",
    ];

    const sampleRows = [
      {
        CampusCode: cc,
        FeeType: "TUITION",
        FeeAmount: 50000,
        Grace: 5,
        DueDate: "2026-01-15",
        IsActive: 1,
      },
      {
        CampusCode: cc,
        FeeType: "HOSTEL",
        FeeAmount: 30000,
        Grace: 3,
        DueDate: "2026-01-20",
        IsActive: 1,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({
      wch: Math.max(14, h.length + 2),
    }));

    const instructions = [
      ["Field", "Notes / Example"],
      ["CampusCode", "Auto-filled from login; do not change."],
      ["FeeType", "Example: TUITION / HOSTEL / TRANSPORT"],
      ["FeeAmount", "Numeric amount, e.g. 50000"],
      ["Grace", "Grace days, e.g. 5"],
      ["DueDate", "YYYY-MM-DD, e.g. 2026-01-15"],
      ["IsActive", "1 = Active, 0 = Inactive"],
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 18 }, { wch: 60 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FeeStructure");
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([out], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `FeeStructure_Sample_${cc}.xlsx`
    );
  };

  const bulkUploadFeeStructure = async (file) => {
    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/UploadFeeStructure`, form, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      setMsg({
        type: "success",
        text: "Fee structure bulk upload completed.",
      });
      await fetchFeeStructures();
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Bulk upload failed.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // FEE BUNDLES
  // ============================================================

  const fetchFeeTypes = async () => {
    const res = await axios.get(`${API_URL}/api/fees/admin/feetypes`, {
      headers: authHeaders,
    });
    setFeeTypes(Array.isArray(res.data) ? res.data : []);
  };

  const fetchBundles = async () => {
    const res = await axios.get(`${API_URL}/api/fees/admin/bundles`, {
      headers: authHeaders,
    });
    setBundles(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await Promise.all([fetchFeeTypes(), fetchBundles()]);
      } catch (e) {
        setMsg({
          type: "error",
          text: e.response?.data?.error || "Failed to load fee bundles.",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const activeFeeTypeUsageMap = useMemo(() => {
    const map = new Map();

    for (const bundle of bundles || []) {
      if (!bundle?.IsActive) continue;
      if (
        bundleDraft.BundleId &&
        bundle.BundleId === bundleDraft.BundleId
      ) {
        continue;
      }

      for (const item of bundle.Items || []) {
        if (item?.FeeType) map.set(item.FeeType, bundle);
      }
    }

    return map;
  }, [bundles, bundleDraft.BundleId]);

  const selectedFeeTypesSet = useMemo(
    () => new Set(bundleDraft.Items.map((x) => x.FeeType)),
    [bundleDraft.Items]
  );

  // FeeType availability for the bundle editor:
  // - New bundle: show only FeeTypes not present in any existing bundle.
  // - Edit bundle: show FeeTypes already in this bundle + FeeTypes unused by every other bundle.
  const feeTypesUsedByOtherBundles = useMemo(() => {
    const used = new Set();

    for (const bundle of bundles || []) {
      if (
        bundleDraft.BundleId &&
        String(bundle.BundleId) === String(bundleDraft.BundleId)
      ) {
        continue;
      }

      for (const item of bundle.Items || []) {
        if (item?.FeeType) used.add(String(item.FeeType));
      }
    }

    return used;
  }, [bundles, bundleDraft.BundleId]);

  const visibleBundleFeeTypes = useMemo(
    () =>
      (feeTypes || []).filter(
        (feeType) =>
          selectedFeeTypesSet.has(feeType) ||
          !feeTypesUsedByOtherBundles.has(String(feeType))
      ),
    [feeTypes, selectedFeeTypesSet, feeTypesUsedByOtherBundles]
  );

  const sortedDraftItems = useMemo(
    () =>
      [...bundleDraft.Items].sort(
        (a, b) =>
          Number(a.Priority) - Number(b.Priority) ||
          String(a.FeeType).localeCompare(String(b.FeeType))
      ),
    [bundleDraft.Items]
  );

  const openNewBundle = () => {
    setBundleDraft(emptyBundle);
    setBundleDialogOpen(true);
  };

  const openEditBundle = (bundle) => {
    setBundleDraft({
      BundleId: bundle.BundleId,
      BundleKey: bundle.BundleKey || "",
      StudentLabel: bundle.StudentLabel || "",
      IsActive: bundle.IsActive ? 1 : 0,
      AllowPartial: bundle.AllowPartial ? 1 : 0,
      AllocationStrategy: bundle.AllocationStrategy || "PRIORITY",
      SortOrder: Number(bundle.SortOrder || 0),
      Items: Array.isArray(bundle.Items)
        ? bundle.Items.map((x) => ({
            FeeType: x.FeeType,
            Priority: Number(x.Priority ?? 100),
          }))
        : [],
    });
    setBundleDialogOpen(true);
  };

  const resetBundleDraft = () => {
    setBundleDraft(emptyBundle);
  };

  const toggleFeeTypeInBundle = (feeType) => {
    const usedBy = activeFeeTypeUsageMap.get(feeType);

    if (usedBy) {
      setMsg({
        type: "error",
        text: `${feeType} is already used in active bundle "${
          usedBy.StudentLabel || usedBy.BundleKey
        }". Deactivate or delete that bundle first.`,
      });
      return;
    }

    setBundleDraft((draft) => {
      const exists = draft.Items.some(
        (item) => item.FeeType === feeType
      );

      if (exists) {
        return {
          ...draft,
          Items: draft.Items.filter(
            (item) => item.FeeType !== feeType
          ),
        };
      }

      return {
        ...draft,
        Items: [
          ...draft.Items,
          { FeeType: feeType, Priority: 100 },
        ],
      };
    });
  };

  const setBundleFeePriority = (feeType, priority) => {
    setBundleDraft((draft) => ({
      ...draft,
      Items: draft.Items.map((item) =>
        item.FeeType === feeType
          ? { ...item, Priority: Number(priority || 0) }
          : item
      ),
    }));
  };

  const saveBundle = async () => {
    const isEditing = Boolean(bundleDraft.BundleId);

    if (!bundleDraft.StudentLabel?.trim()) {
      return setMsg({
        type: "error",
        text: "Student Label is required.",
      });
    }

    if (!isEditing && !bundleDraft.BundleKey?.trim()) {
      return setMsg({
        type: "error",
        text: "Bundle Key is required for a new bundle.",
      });
    }

    if (!bundleDraft.Items.length) {
      return setMsg({
        type: "error",
        text: "Select at least one FeeType.",
      });
    }

    if (bundleDraft.IsActive) {
      const blockedFeeTypes = bundleDraft.Items
        .map((x) => x.FeeType)
        .filter((feeType) =>
          activeFeeTypeUsageMap.has(feeType)
        );

      if (blockedFeeTypes.length) {
        const first = blockedFeeTypes[0];
        const usedBy = activeFeeTypeUsageMap.get(first);
        return setMsg({
          type: "error",
          text: `${first} is already used in active bundle "${
            usedBy.StudentLabel || usedBy.BundleKey
          }". A FeeType cannot be active in multiple bundles.`,
        });
      }
    }

    const payload = {
      BundleId: bundleDraft.BundleId || undefined,
      BundleKey: bundleDraft.BundleKey?.trim(),
      StudentLabel: bundleDraft.StudentLabel?.trim(),
      IsActive: bundleDraft.IsActive ? 1 : 0,
      AllowPartial: bundleDraft.AllowPartial ? 1 : 0,
      AllocationStrategy: bundleDraft.AllocationStrategy,
      SortOrder: Number(bundleDraft.SortOrder || 0),
      Items: bundleDraft.Items.map((x) => ({
        FeeType: x.FeeType,
        Priority: Number(x.Priority ?? 100),
      })),
    };

    try {
      await axios.post(
        `${API_URL}/api/fees/admin/bundles/upsert`,
        payload,
        { headers: authHeaders }
      );

      setMsg({
        type: "success",
        text: isEditing
          ? "Fee bundle updated successfully."
          : "Fee bundle added successfully.",
      });

      await Promise.all([fetchBundles(), fetchFeeTypes()]);
      setBundleDialogOpen(false);
      setBundleDraft(emptyBundle);
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to save fee bundle.",
      });
    }
  };

  const deleteBundle = async (bundle) => {
    if (!bundle?.BundleId) return;

    if (
      !window.confirm(
        `Delete bundle "${bundle.StudentLabel || bundle.BundleKey}"?`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/fees/admin/bundles/${bundle.BundleId}`,
        { headers: authHeaders }
      );

      setMsg({
        type: "success",
        text: "Fee bundle deleted successfully.",
      });

      await fetchBundles();
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to delete fee bundle.",
      });
    }
  };

  // ============================================================
  // PAGINATION
  // ============================================================

  const feeMaxPage = Math.max(
    0,
    Math.ceil(feeStructures.length / feeRowsPerPage) - 1
  );
  const safeFeePage = Math.min(feePage, feeMaxPage);
  const paginatedFees = feeStructures.slice(
    safeFeePage * feeRowsPerPage,
    safeFeePage * feeRowsPerPage + feeRowsPerPage
  );

  const bundleMaxPage = Math.max(
    0,
    Math.ceil(bundles.length / bundleRowsPerPage) - 1
  );
  const safeBundlePage = Math.min(bundlePage, bundleMaxPage);
  const paginatedBundles = bundles.slice(
    safeBundlePage * bundleRowsPerPage,
    safeBundlePage * bundleRowsPerPage + bundleRowsPerPage
  );

  useEffect(() => {
    if (feePage > feeMaxPage) setFeePage(feeMaxPage);
  }, [feePage, feeMaxPage]);

  useEffect(() => {
    if (bundlePage > bundleMaxPage) setBundlePage(bundleMaxPage);
  }, [bundlePage, bundleMaxPage]);

  // ============================================================
  // RENDER: FEE STRUCTURE
  // ============================================================

  const renderFeeStructure = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={sectionSx}>
        <Typography sx={{ fontWeight: 900, color: BLUE }}>
          Fee Structure
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: "0.85rem",
            color: "text.secondary",
          }}
        >
          Maintain fee types, amount, grace period, due date and status.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 920 }}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  <TableCell>Campus</TableCell>
                  <TableCell>Fee Type</TableCell>
                  <TableCell>Fee Amount</TableCell>
                  <TableCell>Grace</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedFees.map((row, index) => {
                  const isEdit = feeEditId === row.id;
                  const serial =
                    safeFeePage * feeRowsPerPage + index + 1;

                  return (
                    <TableRow
                      key={row.id ?? `fee-${index}`}
                      sx={zebraRowSx(index)}
                    >
                      <TableCell>{serialBadge(serial)}</TableCell>

                      <TableCell>{row.CampusCode}</TableCell>

                      <TableCell sx={{ minWidth: 180 }}>
                        {isEdit ? (
                          <TextField
                            size="small"
                            fullWidth
                            value={row.FeeType ?? ""}
                            onChange={(e) =>
                              updateExistingFee(
                                row.id,
                                "FeeType",
                                e.target.value
                              )
                            }
                            sx={inputSx}
                          />
                        ) : (
                          row.FeeType
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 140 }}>
                        {isEdit ? (
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={row.FeeAmount ?? ""}
                            onChange={(e) =>
                              updateExistingFee(
                                row.id,
                                "FeeAmount",
                                e.target.value
                              )
                            }
                            sx={inputSx}
                          />
                        ) : (
                          row.FeeAmount
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 110 }}>
                        {isEdit ? (
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={row.Grace ?? ""}
                            onChange={(e) =>
                              updateExistingFee(
                                row.id,
                                "Grace",
                                e.target.value
                              )
                            }
                            sx={inputSx}
                          />
                        ) : (
                          row.Grace
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 160 }}>
                        {isEdit ? (
                          <TextField
                            size="small"
                            fullWidth
                            type="date"
                            value={convertToISTDate(row.DueDate)}
                            onChange={(e) =>
                              updateExistingFee(
                                row.id,
                                "DueDate",
                                e.target.value
                              )
                            }
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        ) : (
                          convertToISTDate(row.DueDate)
                        )}
                      </TableCell>

                      <TableCell>
                        {isEdit ? (
                          <Checkbox
                            checked={Number(row.IsActive) === 1}
                            onChange={(e) =>
                              updateExistingFee(
                                row.id,
                                "IsActive",
                                e.target.checked ? 1 : 0
                              )
                            }
                            sx={{
                              color: BLUE,
                              "&.Mui-checked": {
                                color: THEME_ORANGE,
                              },
                            }}
                          />
                        ) : (
                          <Checkbox
                            checked={Number(row.IsActive) === 1}
                            disabled
                            sx={{
                              p: 0.5,
                              color: BLUE,
                              "&.Mui-checked": { color: THEME_ORANGE },
                              "&.Mui-disabled": {
                                color:
                                  Number(row.IsActive) === 1
                                    ? `${THEME_ORANGE} !important`
                                    : "rgba(25,118,210,0.35) !important",
                              },
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {isEdit ? (
                          <>
                            <IconButton
                              size="small"
                              sx={iconBtnSx}
                              onClick={() => updateFeeStructure(row)}
                              title="Save"
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                              size="small"
                              sx={iconBtnSx}
                              onClick={() => {
                                setFeeEditId(null);
                                fetchFeeStructures();
                              }}
                              title="Cancel"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              sx={iconBtnSx}
                              onClick={() => setFeeEditId(row.id)}
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                              size="small"
                              sx={dangerIconBtnSx}
                              onClick={() => deleteFeeStructure(row)}
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading && paginatedFees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography
                        sx={{
                          py: 4,
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        No fee structure records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{
                      p: 0,
                      borderBottom: 0,
                      backgroundColor: "rgba(25,118,210,.025)",
                    }}
                  >
                    <Button
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setNewFee(emptyFeeStructure(campusCode));
                        setFeeAddOpen(true);
                      }}
                      sx={{
                        justifyContent: "center",
                        py: 1.35,
                        borderRadius: 0,
                        textTransform: "none",
                        fontWeight: 900,
                        color: THEME_ORANGE,
                        borderTop: `1px solid ${BORDER}`,
                        backgroundColor: "rgba(240,120,40,.035)",
                        "&:hover": {
                          backgroundColor: "rgba(240,120,40,.085)",
                        },
                      }}
                    >
                      Add Fee Structure
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        <TablePagination
          component="div"
          count={feeStructures.length}
          page={safeFeePage}
          onPageChange={(_, p) => setFeePage(p)}
          rowsPerPage={feeRowsPerPage}
          onRowsPerPageChange={(e) => {
            setFeeRowsPerPage(parseInt(e.target.value, 10));
            setFeePage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
          labelRowsPerPage="Rows per page"
          showFirstButton
          showLastButton
        />
      </Paper>
    </Stack>
  );

  // ============================================================
  // RENDER: FEE BUNDLES
  // ============================================================

  const renderBundles = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={sectionSx}>
        <Typography sx={{ fontWeight: 900, color: BLUE }}>
          Fee Bundles
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: "0.85rem",
            color: "text.secondary",
          }}
        >
          Club multiple FeeTypes into a single line item shown to students.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  <TableCell>Student Label</TableCell>
                  <TableCell>Bundle Key</TableCell>
                  <TableCell>Fee Types</TableCell>
                  <TableCell>Allocation</TableCell>
                  <TableCell>Partial</TableCell>
                  <TableCell>Sort Order</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedBundles.map((bundle, index) => {
                  const serial =
                    safeBundlePage * bundleRowsPerPage + index + 1;

                  return (
                    <TableRow
                      key={bundle.BundleId}
                      sx={zebraRowSx(index)}
                    >
                      <TableCell>{serialBadge(serial)}</TableCell>

                      <TableCell sx={{ fontWeight: 800 }}>
                        {bundle.StudentLabel || "—"}
                      </TableCell>

                      <TableCell>{bundle.BundleKey || "—"}</TableCell>

                      <TableCell sx={{ minWidth: 220 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.6,
                            flexWrap: "wrap",
                          }}
                        >
                          {(bundle.Items || []).slice(0, 4).map((item) => (
                            <Chip
                              key={`${bundle.BundleId}-${item.FeeType}`}
                              size="small"
                              label={`${item.FeeType} (P${item.Priority ?? 100})`}
                              sx={{
                                height: 24,
                                fontWeight: 800,
                                backgroundColor:
                                  "rgba(25,118,210,0.10)",
                                border:
                                  "1px solid rgba(25,118,210,0.20)",
                              }}
                            />
                          ))}

                          {(bundle.Items || []).length > 4 && (
                            <Chip
                              size="small"
                              label={`+${bundle.Items.length - 4}`}
                              sx={{
                                height: 24,
                                fontWeight: 900,
                                backgroundColor:
                                  "rgba(240,120,40,0.12)",
                                border:
                                  "1px solid rgba(240,120,40,0.25)",
                                color: THEME_ORANGE,
                              }}
                            />
                          )}

                          {!bundle.Items?.length && "—"}
                        </Box>
                      </TableCell>

                      <TableCell>
                        {bundle.AllocationStrategy || "PRIORITY"}
                      </TableCell>

                      <TableCell>
                        {bundle.AllowPartial ? "Yes" : "No"}
                      </TableCell>

                      <TableCell>
                        {Number(bundle.SortOrder || 0)}
                      </TableCell>

                      <TableCell>
                        <Checkbox
                          checked={Boolean(bundle.IsActive)}
                          disabled
                          sx={{
                            p: 0.5,
                            color: BLUE,
                            "&.Mui-checked": { color: THEME_ORANGE },
                            "&.Mui-disabled": {
                              color: bundle.IsActive
                                ? `${THEME_ORANGE} !important`
                                : "rgba(25,118,210,0.35) !important",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        <IconButton
                          size="small"
                          sx={iconBtnSx}
                          onClick={() => openEditBundle(bundle)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          sx={dangerIconBtnSx}
                          onClick={() => deleteBundle(bundle)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!paginatedBundles.length && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography
                        sx={{
                          py: 4,
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        No fee bundles created yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{
                      p: 0,
                      borderBottom: 0,
                      backgroundColor: "rgba(25,118,210,.025)",
                    }}
                  >
                    <Button
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={openNewBundle}
                      sx={{
                        justifyContent: "center",
                        py: 1.35,
                        borderRadius: 0,
                        textTransform: "none",
                        fontWeight: 900,
                        color: THEME_ORANGE,
                        borderTop: `1px solid ${BORDER}`,
                        backgroundColor: "rgba(240,120,40,.035)",
                        "&:hover": {
                          backgroundColor: "rgba(240,120,40,.085)",
                        },
                      }}
                    >
                      Add Fee Bundle
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={bundles.length}
          page={safeBundlePage}
          onPageChange={(_, p) => setBundlePage(p)}
          rowsPerPage={bundleRowsPerPage}
          onRowsPerPageChange={(e) => {
            setBundleRowsPerPage(parseInt(e.target.value, 10));
            setBundlePage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
          labelRowsPerPage="Rows per page"
          showFirstButton
          showLastButton
        />
      </Paper>
    </Stack>
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* Main orange header - same pattern as AcademicCodeSetup */}
        <Box
          sx={{
            ...headerSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              Student Fee Setup
            </Typography>

            <Typography
              sx={{
                opacity: 0.9,
                fontSize: "0.85rem",
                mt: 0.25,
              }}
            >
              Maintain fee structure and student fee bundles from one page.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {activeTab.key === "structure" ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setNewFee(emptyFeeStructure(campusCode));
                    setFeeAddOpen(true);
                  }}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: "white",
                    color: THEME_ORANGE,
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,.92)",
                    },
                  }}
                >
                  Add Fee Structure
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadFeeSample}
                  sx={{
                    ...outlinedBtnSx,
                    backgroundColor: "white",
                  }}
                >
                  Download Sample
                </Button>

                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={() => feeFileInputRef.current?.click()}
                  sx={primaryBtnSx}
                >
                  Bulk Upload
                </Button>

                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  ref={feeFileInputRef}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    bulkUploadFeeStructure(file);
                    e.target.value = "";
                  }}
                />
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openNewBundle}
                sx={{
                  ...primaryBtnSx,
                  backgroundColor: "white",
                  color: THEME_ORANGE,
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,.92)",
                  },
                }}
              >
                Add Fee Bundle
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2, bgcolor: BG }}>
          <Paper elevation={0} sx={surfaceSx}>
            <Tabs
              value={tab}
              onChange={(_, value) => {
                setTab(value);
                setFeeEditId(null);
                setFeeAddOpen(false);
                setBundleDialogOpen(false);
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={tabsSx}
            >
              {FEE_TABS.map((item) => (
                <Tab
                  key={item.key}
                  label={item.label}
                  icon={item.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>

            <Box sx={{ p: 2 }}>
              {activeTab.key === "structure"
                ? renderFeeStructure()
                : renderBundles()}
            </Box>
          </Paper>
        </Box>
      </Paper>

      {/* ======================================================
          ADD FEE STRUCTURE DIALOG
          ====================================================== */}
      <Dialog
        open={feeAddOpen}
        onClose={() => setFeeAddOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
            boxShadow: "0 18px 55px rgba(15,23,42,.22)",
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              ...headerSx,
              borderRadius: 0,
              px: 3,
              py: 2,
            }}
          >
            <Typography sx={{ fontWeight: 900 }}>
              Add Fee Structure
            </Typography>
            <Typography
              sx={{
                fontSize: "0.82rem",
                opacity: 0.9,
              }}
            >
              Enter the fee type, amount, grace period, due date and status.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: BG,
            p: { xs: 2, sm: 3 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 2.5,
              borderRadius: 2.5,
              border: `1px solid ${BORDER}`,
              borderTop: `3px solid ${THEME_ORANGE}`,
              backgroundColor: "white",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(180px, 1fr))",
                },
                gap: 2,
              }}
            >
              <TextField
                label="Campus"
                value={campusCode}
                disabled
                fullWidth
                size="small"
                sx={inputSx}
              />

              <TextField
                label="Fee Type"
                value={newFee.FeeType}
                onChange={(e) =>
                  setNewFee((prev) => ({
                    ...prev,
                    FeeType: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                sx={inputSx}
              />

              <TextField
                label="Fee Amount"
                type="number"
                value={newFee.FeeAmount}
                onChange={(e) =>
                  setNewFee((prev) => ({
                    ...prev,
                    FeeAmount: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                sx={inputSx}
              />

              <TextField
                label="Grace"
                type="number"
                value={newFee.Grace}
                onChange={(e) =>
                  setNewFee((prev) => ({
                    ...prev,
                    Grace: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                sx={inputSx}
              />

              <TextField
                label="Due Date"
                type="date"
                value={newFee.DueDate}
                onChange={(e) =>
                  setNewFee((prev) => ({
                    ...prev,
                    DueDate: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                sx={inputSx}
                InputLabelProps={{ shrink: true }}
              />

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Checkbox
                  checked={Number(newFee.IsActive) === 1}
                  onChange={(e) =>
                    setNewFee((prev) => ({
                      ...prev,
                      IsActive: e.target.checked ? 1 : 0,
                    }))
                  }
                  sx={{
                    color: BLUE,
                    "&.Mui-checked": {
                      color: THEME_ORANGE,
                    },
                  }}
                />
                <Typography variant="body2">Active</Typography>
              </Stack>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "white",
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <Button
            onClick={() => setFeeAddOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addFeeStructure}
            sx={primaryBtnSx}
          >
            Add Fee Structure
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================
          ADD / EDIT FEE BUNDLE DIALOG
          ====================================================== */}
      <Dialog
        open={bundleDialogOpen}
        onClose={() => setBundleDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
            boxShadow: "0 18px 55px rgba(15,23,42,.22)",
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              ...headerSx,
              borderRadius: 0,
              px: 3,
              py: 2,
            }}
          >
            <Typography sx={{ fontWeight: 900 }}>
              {bundleDraft.BundleId
                ? "Edit Fee Bundle"
                : "Add Fee Bundle"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.82rem",
                opacity: 0.9,
              }}
            >
              Configure the student label, allocation rules and included fee types.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: BG,
            p: { xs: 2, sm: 3 },
          }}
        >
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Paper elevation={0} sx={popupSectionSx}>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: BLUE,
                  mb: 1.5,
                }}
              >
                Bundle Details
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(180px, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  label="Bundle Key"
                  value={bundleDraft.BundleKey}
                  onChange={(e) =>
                    setBundleDraft((prev) => ({
                      ...prev,
                      BundleKey: e.target.value,
                    }))
                  }
                  disabled={Boolean(bundleDraft.BundleId)}
                  helperText={
                    bundleDraft.BundleId
                      ? "Key cannot be changed"
                      : "Example: FEE"
                  }
                  fullWidth
                  size="small"
                  sx={inputSx}
                />

                <TextField
                  label="Student Label"
                  value={bundleDraft.StudentLabel}
                  onChange={(e) =>
                    setBundleDraft((prev) => ({
                      ...prev,
                      StudentLabel: e.target.value,
                    }))
                  }
                  helperText="Example: Fee / Semester Fee"
                  fullWidth
                  size="small"
                  sx={inputSx}
                />

                <TextField
                  label="Sort Order"
                  type="number"
                  value={bundleDraft.SortOrder}
                  onChange={(e) =>
                    setBundleDraft((prev) => ({
                      ...prev,
                      SortOrder: e.target.value,
                    }))
                  }
                  fullWidth
                  size="small"
                  sx={inputSx}
                />

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      mb: 0.6,
                    }}
                  >
                    Allocation Strategy
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={bundleDraft.AllocationStrategy}
                    onChange={(e) =>
                      setBundleDraft((prev) => ({
                        ...prev,
                        AllocationStrategy: e.target.value,
                      }))
                    }
                    sx={inputSx}
                  >
                    <MenuItem value="PRIORITY">
                      PRIORITY
                    </MenuItem>
                    <MenuItem value="PROPORTIONAL">
                      PROPORTIONAL
                    </MenuItem>
                  </Select>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(bundleDraft.IsActive)}
                      onChange={(e) =>
                        setBundleDraft((prev) => ({
                          ...prev,
                          IsActive: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label="Active"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(bundleDraft.AllowPartial)}
                      onChange={(e) =>
                        setBundleDraft((prev) => ({
                          ...prev,
                          AllowPartial: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label="Allow Partial"
                />
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                ...surfaceSx,
                overflow: "hidden",
                borderTop: `4px solid ${THEME_ORANGE}`,
                borderRadius: 2.5,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: BLUE,
                  }}
                >
                  Choose Fee Types
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: "text.secondary",
                  }}
                >
                  {bundleDraft.BundleId
                    ? "Showing FeeTypes already in this bundle plus FeeTypes not assigned to any other bundle."
                    : "Showing only FeeTypes that are not assigned to any existing bundle."}
                </Typography>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 620 }}>
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell>Include</TableCell>
                      <TableCell>Fee Type</TableCell>
                      <TableCell>Priority</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {visibleBundleFeeTypes.map((feeType, index) => {
                      const checked =
                        selectedFeeTypesSet.has(feeType);
                      const current = bundleDraft.Items.find(
                        (item) => item.FeeType === feeType
                      );
                      const usedBy =
                        activeFeeTypeUsageMap.get(feeType);
                      const blocked = Boolean(usedBy) && !checked;

                      return (
                        <TableRow
                          key={feeType}
                          sx={zebraRowSx(index)}
                        >
                          <TableCell>
                            <Switch
                              checked={checked}
                              disabled={blocked}
                              onChange={() =>
                                toggleFeeTypeInBundle(feeType)
                              }
                            />
                          </TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>
                            {feeType}

                            {blocked && (
                              <Chip
                                size="small"
                                label={`Used in ${
                                  usedBy.StudentLabel ||
                                  usedBy.BundleKey
                                }`}
                                sx={{
                                  ml: 1,
                                  height: 22,
                                  fontWeight: 800,
                                  backgroundColor:
                                    "rgba(244,67,54,0.08)",
                                  border:
                                    "1px solid rgba(244,67,54,0.25)",
                                  color:
                                    "rgba(244,67,54,0.90)",
                                }}
                              />
                            )}
                          </TableCell>

                          <TableCell>
                            <TextField
                              value={
                                checked
                                  ? current?.Priority ?? 100
                                  : ""
                              }
                              onChange={(e) =>
                                setBundleFeePriority(
                                  feeType,
                                  e.target.value
                                )
                              }
                              size="small"
                              type="number"
                              disabled={!checked}
                              sx={{
                                ...inputSx,
                                maxWidth: 220,
                              }}
                              placeholder="100"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {!visibleBundleFeeTypes.length && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          sx={{
                            py: 4,
                            textAlign: "center",
                            color: "text.secondary",
                          }}
                        >
                          No available FeeTypes found. All FeeTypes are already assigned to other bundles.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Paper>

            <Paper elevation={0} sx={popupSectionSx}>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: BLUE,
                  mb: 1,
                }}
              >
                Student Preview
              </Typography>

              <Typography
                sx={{
                  fontWeight: 900,
                  color: THEME_ORANGE,
                }}
              >
                {bundleDraft.StudentLabel || "Fee"}
              </Typography>

              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: "0.9rem",
                  color: "text.secondary",
                }}
              >
                Includes:{" "}
                {sortedDraftItems.length
                  ? sortedDraftItems
                      .map(
                        (item) =>
                          `${item.FeeType} (P${item.Priority})`
                      )
                      .join(", ")
                  : "—"}
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "white",
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <Button
            startIcon={<RestartAltIcon />}
            onClick={resetBundleDraft}
            sx={{
              textTransform: "none",
              fontWeight: 800,
            }}
          >
            Clear
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={() => setBundleDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveBundle}
            sx={primaryBtnSx}
          >
            {bundleDraft.BundleId
              ? "Save Changes"
              : "Add Fee Bundle"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(msg.text)}
        autoHideDuration={3500}
        onClose={() =>
          setMsg((prev) => ({ ...prev, text: "" }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={msg.type}
          onClose={() =>
            setMsg((prev) => ({ ...prev, text: "" }))
          }
          sx={{ borderRadius: 2 }}
        >
          {msg.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
